// The resume token store (deployment.md §3 step 5).
//
// A resume token names one **seat**, and a seat belongs to one world, so
// the store is keyed by world id: `aquaphobia.token.<worldId>`. One browser
// can therefore hold a seat in several worlds at once, which the single
// `aquaphobia.token` key of every build before this could not.
//
// The key is the world the client **dials**, not the world the hello names.
// A connection has to read the token before it has a hello, so the two must
// agree, and the dialled world is the only one known at that moment. World
// id 0 is "whatever world this server serves by default" — the legacy
// direct mode with no `?world=` in the URL.
//
// Every access goes through a try/catch: private mode and a full store both
// throw, and forgetting a token only means the client founds again rather
// than resumes. Nothing here talks to the network, so it is all testable.

import { storeOf } from '../ui/localstore';

/** The single key every build before the per-world scheme wrote. */
export const LEGACY_TOKEN_KEY = 'aquaphobia.token';

/** The store this module needs. `localStorage` satisfies it. */
export interface TokenStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Key enumeration as `localStorage` has it. A store without it loses
   * only `clearAllTokens`' sweep of the per-world keys. */
  readonly length?: number;
  key?(index: number): string | null;
}

/** The per-world key. */
export function tokenKey(worldId: number): string {
  return `${LEGACY_TOKEN_KEY}.${worldId}`;
}


/**
 * Reads the token for one world, and migrates the legacy key on the way.
 *
 * The migration runs once: when this world has no token of its own and the
 * single old key is still there, that value becomes this world's token and
 * the old key is removed. A player who reloads into the same lane therefore
 * keeps the empire it was playing, which is what the legacy direct mode
 * needs. A second world starts with no token and founds a seat of its own.
 */
export function loadToken(worldId: number, store: TokenStore | null = storeOf()): string {
  if (!store) return '';
  try {
    const own = store.getItem(tokenKey(worldId));
    if (own) return own;
    const legacy = store.getItem(LEGACY_TOKEN_KEY);
    if (!legacy) return '';
    store.setItem(tokenKey(worldId), legacy);
    store.removeItem(LEGACY_TOKEN_KEY);
    return legacy;
  } catch {
    return '';
  }
}

/** Writes the token for one world. An empty token is never stored. */
export function saveToken(worldId: number, token: string,
  store: TokenStore | null = storeOf()): void {
  if (!store || !token) return;
  try {
    store.setItem(tokenKey(worldId), token);
  } catch { /* private mode, a full store: the seat lasts the session */ }
}

/**
 * Forgets the token for one world: a log out, or a seat the registry can no
 * longer find.
 *
 * The legacy key goes with it. It has to: `loadToken` migrates that key into
 * whichever world reads first, so a legacy token left behind would put the
 * player back in the seat the log out just left.
 */
export function clearToken(worldId: number, store: TokenStore | null = storeOf()): void {
  if (!store) return;
  try {
    store.removeItem(tokenKey(worldId));
    store.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* private mode, a full store: nothing was persisted to forget */ }
}

/**
 * Forgets every world's token and the legacy key: the browser's signed
 * identity changed (a sign-in to another account, a sign-out, a fresh
 * guest), so no seat the store remembers belongs to the player any more.
 * A stale token would otherwise ride the next dial and resume the OLD
 * identity's seat — the server refuses that when a ticket rides along,
 * but the store must not keep offering it.
 */
export function clearAllTokens(store: TokenStore | null = storeOf()): void {
  if (!store) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < (store.length ?? 0); i++) {
      const k = store.key?.(i);
      if (k?.startsWith(`${LEGACY_TOKEN_KEY}.`)) doomed.push(k);
    }
    for (const k of doomed) store.removeItem(k);
    store.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* private mode, a full store: nothing was persisted to forget */ }
}

/**
 * The world a websocket URL dials, from its `?world=` parameter. The join
 * answer always carries one (R143 §9); the legacy direct mode usually does
 * not, and then the answer is 0 — the server's own default world.
 */
export function worldIdFromUrl(url: string): number {
  const q = url.indexOf('?');
  if (q < 0) return 0;
  const raw = new URLSearchParams(url.slice(q + 1)).get('world');
  if (raw === null) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}
